// doc/12 §3.2.2: graph snapshot share card. Renders the current graph
// view into a 1080×1440 portrait PNG — warm-paper background, project
// name, card/decision stats, and a QR code pointing at the public
// share link — sized for a WeChat Moments post. Everything is composed
// client-side on a <canvas>; no server-side rendering involved.
//
// Flow on open: reuse (or create) the project's graph share link →
// toPng() the live reactflow viewport → QR-encode the link → compose →
// show a preview with "save image" / "copy link".

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toPng } from 'html-to-image';
import QRCode from 'qrcode';
import { api } from '../../api';

// Canvas colors mirror styles/tokens.css — canvas can't resolve CSS
// variables, so the warm-paper palette is duplicated here by value.
const PAPER_0 = '#FCFBF9';
const INK_0 = '#1A1814';
const INK_2 = '#6B6259';
const INK_3 = '#9B9389';
const RULE = '#E8E4DB';
const AMBER = '#B8691A';

const CARD_W = 1080;
const CARD_H = 1440;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('image decode failed'));
    img.src = src;
  });
}

/** fillText with a trailing ellipsis when `text` exceeds `maxW`. */
function fillTextEllipsis(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxW: number,
) {
  if (ctx.measureText(text).width <= maxW) {
    ctx.fillText(text, x, y);
    return;
  }
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxW) {
    s = s.slice(0, -1);
  }
  ctx.fillText(`${s}…`, x, y);
}

/** Rounded-rect path helper — avoids relying on ctx.roundRect typings. */
function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

async function composeShareCard(opts: {
  graphPng: string;
  qrPng: string;
  projectName: string;
  statsLine: string;
  tagline: string;
  domain: string;
}): Promise<string> {
  // Make sure Fraunces / Inter / JetBrains Mono are usable on the canvas
  // before measuring text — they're loaded by the app shell already, this
  // just guards the cold-start race.
  await document.fonts.ready;

  const canvas = document.createElement('canvas');
  canvas.width = CARD_W;
  canvas.height = CARD_H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('canvas 2d context unavailable');

  // Warm paper background + hairline frame.
  ctx.fillStyle = PAPER_0;
  ctx.fillRect(0, 0, CARD_W, CARD_H);
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 2;
  ctx.strokeRect(24, 24, CARD_W - 48, CARD_H - 48);

  // Brand line. Manual letter-spacing (spaces) — ctx.letterSpacing is
  // still patchy across browsers.
  ctx.fillStyle = INK_3;
  ctx.font = "700 20px 'JetBrains Mono', 'SF Mono', monospace";
  ctx.fillText('D R A F T L I Z E', 72, 104);

  // Project name — the headline of the card.
  ctx.fillStyle = INK_0;
  ctx.font = "500 58px Fraunces, 'New York', Georgia, serif";
  fillTextEllipsis(ctx, opts.projectName, 72, 188, CARD_W - 144);

  // Graph screenshot, contain-fit + centered. The screenshot already
  // carries the same paper background so it blends in without a border.
  const graphImg = await loadImage(opts.graphPng);
  const box = { x: 72, y: 248, w: CARD_W - 144, h: 836 };
  const scale = Math.min(box.w / graphImg.width, box.h / graphImg.height);
  const dw = graphImg.width * scale;
  const dh = graphImg.height * scale;
  ctx.drawImage(
    graphImg,
    box.x + (box.w - dw) / 2,
    box.y + (box.h - dh) / 2,
    dw,
    dh,
  );

  // Footer divider — stops short of the QR plate.
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(72, 1136);
  ctx.lineTo(744, 1136);
  ctx.stroke();

  // Stats + tagline + domain (left column).
  ctx.fillStyle = INK_0;
  ctx.font = "600 34px Inter, -apple-system, sans-serif";
  fillTextEllipsis(ctx, opts.statsLine, 72, 1214, 660);
  ctx.fillStyle = INK_2;
  ctx.font = "400 26px Inter, -apple-system, sans-serif";
  fillTextEllipsis(ctx, opts.tagline, 72, 1266, 660);
  ctx.fillStyle = AMBER;
  ctx.font = "600 23px 'JetBrains Mono', 'SF Mono', monospace";
  fillTextEllipsis(ctx, opts.domain, 72, 1330, 660);

  // QR plate (bottom-right) — white backing keeps scan contrast on paper.
  const qrImg = await loadImage(opts.qrPng);
  roundRectPath(ctx, 776, 1128, 232, 232, 12);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();
  ctx.strokeStyle = RULE;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.drawImage(qrImg, 788, 1140, 208, 208);

  return canvas.toDataURL('image/png');
}

/** Reuse the project's existing live graph share, or mint one. Keeps
 *  repeated opens of the modal from piling up rows in `shares`. */
async function ensureGraphShare(projectId: string): Promise<string> {
  try {
    const existing = await api.listShares(projectId);
    const live = existing.find((s) => s.kind === 'graph' && !s.revoked);
    if (live) return `/s/${live.share_id}`;
  } catch {
    // list failing is non-fatal — fall through to create.
  }
  const created = await api.createShare(projectId, 'graph');
  return created.url;
}

type BuildState =
  | { phase: 'building' }
  | { phase: 'ready'; dataUrl: string; shareUrl: string }
  | { phase: 'error'; message: string };

export function GraphShareCardModal({
  projectId,
  projectName,
  cardCount,
  decisionCount,
  graphEl,
  onClose,
}: {
  projectId: string;
  projectName: string;
  cardCount: number;
  decisionCount: number;
  /** Container that holds the live `.react-flow__viewport` to screenshot. */
  graphEl: HTMLElement | null;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [state, setState] = useState<BuildState>({ phase: 'building' });
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const sharePath = await ensureGraphShare(projectId);
        const shareUrl = `${window.location.origin}${sharePath}`;
        const viewport = graphEl?.querySelector('.react-flow__viewport');
        if (!(viewport instanceof HTMLElement)) {
          throw new Error('graph viewport not found');
        }
        const graphPng = await toPng(viewport, {
          backgroundColor: PAPER_0,
          pixelRatio: 2,
          cacheBust: true,
        });
        const qrPng = await QRCode.toDataURL(shareUrl, {
          width: 416,
          margin: 1,
          color: { dark: INK_0, light: '#FFFFFF' },
        });
        const statsLine = `${t('graphShare.statsCards', { count: cardCount })} · ${t('graphShare.statsDecisions', { count: decisionCount })}`;
        const dataUrl = await composeShareCard({
          graphPng,
          qrPng,
          projectName,
          statsLine,
          tagline: t('graphShare.tagline'),
          domain: window.location.host,
        });
        if (!cancelled) setState({ phase: 'ready', dataUrl, shareUrl });
      } catch (e) {
        if (!cancelled) {
          setState({
            phase: 'error',
            message: e instanceof Error ? e.message : String(e),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Build exactly once per open — the modal unmounts on close.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePng = () => {
    if (state.phase !== 'ready') return;
    const a = document.createElement('a');
    a.href = state.dataUrl;
    a.download = `draftlize-card-${projectId}.png`;
    a.click();
  };

  const copyLink = async () => {
    if (state.phase !== 'ready') return;
    await navigator.clipboard.writeText(state.shareUrl).catch(() => {});
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const btnStyle: React.CSSProperties = {
    fontSize: 11,
    padding: '6px 14px',
    border: '1px solid var(--rule)',
    borderRadius: 4,
    background: 'white',
    color: 'var(--ink-1)',
    cursor: 'pointer',
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        background: 'rgba(26, 24, 20, 0.45)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--paper-0)',
          border: '1px solid var(--rule)',
          borderRadius: 8,
          padding: 14,
          width: 'min(420px, 92vw)',
          maxHeight: '92vh',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          boxShadow: '0 12px 40px rgba(0,0,0,0.18)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span
            className="mono"
            style={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '.1em',
              color: 'var(--ink-3)',
            }}
          >
            {t('graphShare.title')}
          </span>
          <span style={{ flex: 1 }} />
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="mono"
            style={{ fontSize: 14, color: 'var(--ink-3)', cursor: 'pointer' }}
          >
            ×
          </button>
        </div>

        {state.phase === 'building' && (
          <div
            className="pulse"
            style={{
              height: 280,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              color: 'var(--ink-3)',
              border: '1px dashed var(--rule)',
              borderRadius: 6,
            }}
          >
            {t('graphShare.building')}
          </div>
        )}

        {state.phase === 'error' && (
          <div
            style={{
              padding: '14px 12px',
              fontSize: 12,
              color: 'var(--red)',
              border: '1px dashed var(--rule)',
              borderRadius: 6,
            }}
          >
            {t('graphShare.error', { error: state.message })}
          </div>
        )}

        {state.phase === 'ready' && (
          <>
            <img
              src={state.dataUrl}
              alt={t('graphShare.title')}
              style={{
                width: '100%',
                borderRadius: 6,
                border: '1px solid var(--rule)',
                // 1080×1440 is taller than most viewports allow — cap and
                // let the aspect ratio drive the width.
                maxHeight: 'calc(92vh - 130px)',
                objectFit: 'contain',
                background: PAPER_0,
              }}
            />
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button onClick={savePng} className="mono" style={{ ...btnStyle, fontWeight: 600, color: 'var(--ink-0)', borderColor: 'var(--ink-0)' }}>
                ⬇ {t('graphShare.savePng')}
              </button>
              <button onClick={copyLink} className="mono" style={btnStyle}>
                {linkCopied ? `✓ ${t('graphShare.copied')}` : `⧉ ${t('graphShare.copyLink')}`}
              </button>
              <span style={{ flex: 1 }} />
              <span className="mono" style={{ fontSize: 9, color: 'var(--ink-4)' }}>
                {t('graphShare.sizeHint')}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
